import React, { useState } from 'react';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from "@/lib/utils";
import { ContextualHelpButton } from "@/components/common/ContextualHelpButton";
import { AdvancedFilterPanel, type FilterFieldDef, type FilterCondition } from './AdvancedFilterPanel';

interface ListPageContainerProps {
    children: React.ReactNode;
    /** Left side: Quick filters (status pills, etc.) */
    filterContent?: React.ReactNode;
    /** Right side: Actions (Import, Export, Add) */
    actions?: React.ReactNode;
    /** Selected count for bulk action bar */
    selectedCount?: number;
    /** Bulk actions when items are selected */
    bulkActions?: React.ReactNode;
    /** Loading state */
    loading?: boolean;
    /** Additional className */
    className?: string;
    /** Module ID for Contextual Help */
    helpKey?: string;
    /** Advanced filter field definitions */
    filterFields?: FilterFieldDef[];
    /** Advanced filter conditions (controlled) */
    filterConditions?: FilterCondition[];
    /** Callback when advanced filter conditions change */
    onFilterChange?: (conditions: FilterCondition[]) => void;
}

/**
 * ListPageContainer — Compact layout for List/Table views (MISA-style dense)
 * 
 * Layout:
 * 1. Action Bar: [Quick Filters + Filter Toggle] | [Actions] — compact
 * 2. Advanced Filter Panel (collapsible, multi-condition)
 * 3. Content (Table with sticky header)
 */
export function ListPageContainer({
    children,
    filterContent,
    actions,
    selectedCount = 0,
    bulkActions,
    loading = false,
    className,
    helpKey,
    filterFields,
    filterConditions,
    onFilterChange,
}: ListPageContainerProps) {
    const hasSelection = selectedCount > 0;
    const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
    const hasAdvancedFilter = !!filterFields && filterFields.length > 0;
    const activeFilterCount = filterConditions?.filter(c => c.value).length || 0;

    return (
        <div className={cn("flex flex-col gap-1 animate-in fade-in duration-300 p-1 md:p-2", className)}>
            {/* Action Bar — compact */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 bg-white px-2 py-1 rounded-lg border border-slate-200 shadow-sm apv-action-bar">
                {/* Left: Filters or Selection Info */}
                <div className="flex items-center gap-1.5 flex-wrap w-full sm:w-auto sm:gap-2">
                    {hasSelection ? (
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <span className="text-xs font-bold apv-accent-text whitespace-nowrap">
                                Đã chọn {selectedCount}
                            </span>
                            {bulkActions}
                        </div>
                    ) : (
                        <>
                            {filterContent}
                            {hasAdvancedFilter && (
                                <Button
                                    variant={showAdvancedFilter ? "default" : "outline"}
                                    size="sm"
                                    className={cn("h-7 text-xs gap-1 px-2", showAdvancedFilter && "apv-btn-accent")}
                                    onClick={() => setShowAdvancedFilter(!showAdvancedFilter)}
                                >
                                    <Filter className="h-3 w-3" />
                                    Lọc
                                    {activeFilterCount > 0 && (
                                        <span className="ml-0.5 bg-white/20 text-[10px] px-1 rounded-full font-bold">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </Button>
                            )}
                        </>
                    )}
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-1.5">
                    {!hasSelection && actions}
                    {helpKey && (
                        <>
                            <div className="h-4 w-px bg-slate-200 mx-0.5 hidden sm:block" />
                            <ContextualHelpButton moduleKey={helpKey} />
                        </>
                    )}
                </div>
            </div>

            {/* Advanced Filter Panel (collapsible) */}
            {hasAdvancedFilter && showAdvancedFilter && filterConditions && onFilterChange && (
                <AdvancedFilterPanel
                    fields={filterFields}
                    conditions={filterConditions}
                    onChange={onFilterChange}
                    standalone={false}
                    className="animate-in slide-in-from-top-2 duration-200"
                />
            )}

            {/* Content Area */}
            <div className={cn(
                loading && "opacity-60 pointer-events-none transition-opacity"
            )}>
                {children}
            </div>
        </div>
    );
}
